<script lang="ts" setup>
import {computed, onMounted, ref, watchEffect} from 'vue';
import {getVueLink, useRouterSectionParams} from '@/ts/composables/stateful/use-route-params';
import {useSessionId} from '@/ts/composables/stateful/use-session';
import {useInterventionStore} from '@/ts/store/intervention-store';
import {pagesPath} from '@/ts/router/pages-path';
import {useRoute, useRouter} from 'vue-router';
import SessionCompletedToc from '@/vue/organisms/session-completed-toc.vue';
import SessionCompletedContent from '@/vue/organisms/session-completed-content.vue';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import GoodJobAnimation from '@/vue/molecules/good-job-animation.vue';
import {PageState, SessionState} from '@/ts/types/dto/interventions-dto';
import {SessionCompletedScenario} from '@/ts/types/session-completed-scenario';
import AllianzBrandingFooter from '@/vue/molecules/allianz-branding-footer.vue';
import {useAllianzBranding} from '@/ts/composables/stateful/use-allianz-branding';
import {todaySessionTaskId, useTodayTaskCompletion} from '@/ts/composables/stateful/use-today-task-completion';

const route = useRoute();
const router = useRouter();
const {isAllianzUser} = useAllianzBranding();
const taskCompletion = useTodayTaskCompletion();
const intervention = useInterventionStore();
const {sid, iid} = useRouterSectionParams();
const {session} = useSessionId(sid);
const showCelebration = ref(false);

// A session is genuinely completed when its state says so, or every page is done.
const isSessionCompleted = computed((): boolean => {
  if (!session.value) {
    return false;
  }
  if (session.value.state === SessionState.COMPLETED) {
    return true;
  }
  const pages = session.value.pages ?? [];
  return pages.length > 0 && pages.every(page => page.state === PageState.COMPLETED);
});

// Guard: the celebration page must only render for a genuinely completed session.
// Blocks URL manipulation onto /completed for locked or in-progress sessions (IPD-2751).
watchEffect(() => {
  if (!intervention.value) {
    return; // intervention still loading
  }
  if (!session.value) {
    void router.replace(pagesPath.main.notFound);
    return;
  }
  if (!isSessionCompleted.value) {
    void router.replace(getVueLink(pagesPath.intervention.interventionLanding, {iid: iid.value}));
  }
});
const celebrationMode = ref<'good-job' | 'complete'>('good-job');
const hasShownAnimation = ref(false);

// Detect scenario based on query params and next session state
const scenario = computed((): SessionCompletedScenario => {
  if (!intervention.value || !session.value) {
    return SessionCompletedScenario.NEXT_PENDING;
  }

  // Check if this is a "just completed" scenario based on query parameter
  if (route.query.just_completed === 'true') {
    return SessionCompletedScenario.JUST_COMPLETED;
  }

  const currentSessionIndex = intervention.value.sessions.findIndex(s => s.id === session.value!.id);
  const nextSession = intervention.value.sessions[currentSessionIndex + 1];

  // Check if next session is completed
  if (nextSession?.state === SessionState.COMPLETED) {
    return SessionCompletedScenario.NEXT_COMPLETED;
  }

  // Default: next session pending (or no next session)
  return SessionCompletedScenario.NEXT_PENDING;
});

const shouldShowAnimation = computed(() => {
  return !hasShownAnimation.value && session.value?.state === SessionState.COMPLETED;
});

const onCelebrationComplete = (): void => {
  hasShownAnimation.value = true;
};

onMounted(() => {
  // A session opened from the Today page and finished (IPD-2974). Three conditions, and each one
  // is load-bearing: the session is genuinely complete (the scenario below trusts the query, so a
  // hand-typed `just_completed` on an unfinished session would otherwise report a completion the
  // guard above is redirecting away from), this is the arrival that finished it rather than a
  // later visit, and the task is the one the Today page marked.
  if (isSessionCompleted.value && scenario.value === SessionCompletedScenario.JUST_COMPLETED) {
    taskCompletion.reportCompleted(todaySessionTaskId(sid.value), 'session');
  }

  if (shouldShowAnimation.value) {
    if (scenario.value === SessionCompletedScenario.JUST_COMPLETED) {
      celebrationMode.value = 'good-job';
      showCelebration.value = true;
    } else if (session.value?.state === SessionState.COMPLETED) {
      celebrationMode.value = 'complete';
      showCelebration.value = true;
    }
  }
});
</script>

<template>
  <div v-if="intervention && session && isSessionCompleted" class="intervention-session-completed-page">
    <div class="toc-container">
      <session-completed-toc/>
    </div>

    <div class="content-container">
      <div class="content-inner">
        <!-- Main Card (common to all scenarios) -->
        <div class="completed-header-card">
          <badge-pill theme="white" :label="$t('completed')" class="completion-badge"/>

          <div class="image-section">
            <div v-if="showCelebration" class="success-content">
              <good-job-animation
                @animation-complete="onCelebrationComplete"
              />
            </div>
            <div v-else class="success-content">
              <svg-illustration name="thumb-up.svg"/>
            </div>
            <span class="good-job-text">{{ celebrationMode === 'complete' ? $t('complete') : $t('good_job') }}</span>
          </div>

          <div class="session-info">
            <span class="session-completed-text">
              {{ $t('session_completed') }}
            </span>
            <h1 class="session-title">
              {{ session.name }}
            </h1>
          </div>

          <svg-illustration name="right-down-arrow.svg" inner-class="inner-right" class="right-down-arrow-element"/>
        </div>

        <!-- Variable content according to scenario -->
        <session-completed-content
          :scenario="scenario"
          :session="session"
          :intervention="intervention"
        />
      </div>
      <allianz-branding-footer v-if="isAllianzUser"/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.intervention-session-completed-page {
  padding: var(--Spacing-spacing-5);
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-5);

  @include screen-lg {
    flex-direction: row;
    gap: 0;
    height: calc(100vh - var(--header-height, 0px));
    max-height: calc(100vh - var(--header-height, 0px));
    overflow: hidden;
    padding-bottom: 0;
  }
}

.toc-container {
  @include screen-lg {
    @include column-width(2);
    min-width: 250px;
    max-height: 100%;
    padding-right: var(--Spacing-spacing-1);
    padding-bottom: var(--Spacing-spacing-9);
    overflow-y: auto;
    overflow-x: hidden;
    flex-shrink: 0;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    transition: scrollbar-color 0.3s;

    &:hover,
    &:active {
      scrollbar-color: var(--Neutral-UI-neutral-ui-40) transparent;
    }
  }
  @include screen-xl {
    padding-right: var(--Spacing-spacing-3);
  }
}

.content-container {
  flex: 1;
  min-width: 0;

  @include screen-lg {
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    transition: scrollbar-color 0.3s;

    &:hover,
    &:active {
      scrollbar-color: var(--Neutral-UI-neutral-ui-40) transparent;
    }
  }
}

.content-inner {
  max-width: 1080px;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-6);
  padding-bottom: var(--Spacing-spacing-6);
  margin: 0 auto;

  @include screen-lg {
    @include page-alignment(1080px);
    padding-bottom: var(--Spacing-spacing-9);
  }
}

.completed-header-card {
  @include q-card-dark-completed;
  padding: var(--Spacing-spacing-6);
  display: flex;
  flex-direction: column;
  position: relative;
  gap: var(--Spacing-spacing-5);
}

.completion-badge {
  position: absolute;
  top: var(--Spacing-spacing-6);
  right: var(--Spacing-spacing-6);
  z-index: 1;
}

.right-down-arrow-element {
  position: absolute;
  bottom: var(--Spacing-spacing-6);
  right: var(--Spacing-spacing-6);
  z-index: 1;
}

.image-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  flex: 1;
}

.session-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--Spacing-spacing-1);
}

.success-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  max-height: 200px;

  @include screen-md {
    width: 250px;
    max-height: 250px;
  }

  @include screen-lg {
    width: 300px;
    max-height: 300px;
  }
}

.good-job-text {
  @include font-display-1;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-0);
  margin: 0;
  text-align: center;
}

.session-completed-text {
  @include font-caption-sm;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-0);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
}

.session-title {
  @include font-display-4;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-0);
  margin: 0;
  line-height: 1.1;
  word-break: break-word;
  overflow-wrap: break-word;
}

</style>